import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Pool, SectionStatus } from "../../shared/types";
import {
  CLOSED_SEASON_BADGE,
  GEO_DENIED_COPY,
  GEO_LABEL_IDLE,
  GEO_LABEL_LOCATED,
  GEO_LABEL_LOCATING,
  SLOPED_ENTRY_BADGE,
  addressHtml,
  effectiveStatus,
  esc,
  geoButtonHtml,
} from "../util";
import type { EffectiveStatus } from "../util";
import { cachedStatus, loadAllStatuses, loadPools } from "../store";
import { matchesStaticFilters, parseFilters } from "../filters";
import { viewTabs } from "./list";
import { CITY } from "../city";
import { cachedLocation, getUserLocation, hasLocationOptIn, lastLocationError } from "../geo";
import type { LatLng } from "../geo";

// Marker color scheme (map view only — does NOT govern list/detail badge colors):
// open reads blue, closed/conflict red, closed-for-season & unknown grey.
const MARKER_OPEN = "#2563eb";
const MARKER_CLOSED = "#dc2626";
const MARKER_NEUTRAL = "#94a3b8";

/**
 * The status section a map marker reads: the children's section for a normal
 * pool that has one, the `main` section for everything else (splash pads and
 * main-only pools that have no kiddie section). This must mirror what the list
 * and detail views render as the primary section for each pool type.
 */
export function markerStatus(pool: Pool): SectionStatus | undefined {
  const section = pool.kind === "splash" || !pool.hasKiddiePool ? "main" : "kiddie";
  return cachedStatus(pool.poolId)?.sections.find((s) => s.section === section);
}

/** Build tooltip HTML: pool name + the relevant section status line. */
function tooltipHtml(pool: Pool): string {
  // A neutral descriptor line for sloped-entry pools (gradual zero-depth entry +
  // wading area for small children), appended below the status line on every
  // non-season-closed tooltip.
  const sloped = pool.slopedEntry ? `<br>${SLOPED_ENTRY_BADGE}` : "";
  if (pool.closedForSeason) {
    return `<strong>${esc(pool.name)}</strong><br>${CLOSED_SEASON_BADGE}${sloped}`;
  }
  const label =
    pool.kind === "splash" ? "Splash pad" : pool.hasKiddiePool ? "Children's pool" : "Main pool";
  const s = markerStatus(pool);
  if (!s) {
    return `<strong>${esc(pool.name)}</strong><br>Status loading…${sloped}`;
  }
  return `<strong>${esc(pool.name)}</strong><br>${label}: ${effectiveStatus(s)}${sloped}`;
}

/**
 * Pure status → marker color. Exported for unit testing. Map-view scoped:
 * open=blue, closed/conflict=red, closed-for-season/unknown=grey.
 */
export function markerColorFor(
  closedForSeason: boolean,
  status: EffectiveStatus | undefined,
): string {
  if (closedForSeason) return MARKER_NEUTRAL; // grey — closed for the season
  if (status === "closed") return MARKER_CLOSED; // red — closed (incl. conflict)
  if (status === "open") return MARKER_OPEN; // blue — open
  return MARKER_NEUTRAL; // grey — unknown / no status
}

/** Color a marker by the pool's relevant section status. */
function markerColor(pool: Pool): string {
  const closedForSeason = !!pool.closedForSeason;
  const s = closedForSeason ? undefined : markerStatus(pool);
  return markerColorFor(closedForSeason, s ? effectiveStatus(s) : undefined);
}

// Marker pins share viewBox 0 0 24 26; height tracks width by the 26/24 ratio.
// `fill="currentColor"` so the wrapping span's `color` sets the status tint.
const PIN_RATIO = 26 / 24;

/** Round-bodied pin with stacked water lines — the children's-pool glyph. */
function poolPinSvg(px: number): string {
  const h = Math.round(px * PIN_RATIO);
  return `<svg class="pin-svg" viewBox="0 0 24 26" width="${px}" height="${h}" aria-hidden="true"><path d="M12 25.1C10.8 23.3 3.7 16.5 3.7 10.2a8.3 8.3 0 1 1 16.6 0c0 6.3-7.1 13.1-8.3 14.9Z" fill="currentColor" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path><path d="M7.8 8.8q.9-.7 1.8 0t1.8 0t1.8 0t1.8 0t1.8 0 M7.8 11.7q.9-.7 1.8 0t1.8 0t1.8 0t1.8 0t1.8 0 M8.6 14.6q.85-.6 1.7 0t1.7 0t1.7 0t1.7 0" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
}

/** Teardrop pin with a spray glyph — the splash-pad marker. */
function splashPinSvg(px: number): string {
  const h = Math.round(px * PIN_RATIO);
  return `<svg class="pin-svg" viewBox="0 0 24 26" width="${px}" height="${h}" aria-hidden="true"><path d="M12 1.5C6.2 1.5 2 5.7 2 11.2c0 6.4 6.9 11.6 10 13.3 3.1-1.7 10-6.9 10-13.3C22 5.7 17.8 1.5 12 1.5Z" fill="currentColor" stroke="#fff" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path><path d="M8.5 15.8h7M12 14.3V8.1M11 14.1c-.7-2-2-3.5-3.8-4.5M13 14.1c.7-2 2-3.5 3.8-4.5" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
}

/**
 * Circular "you are here" icon — a blue filled dot with a white ring, visually
 * distinct from the pool/splash pin glyphs so it reads as the user's own position
 * rather than a venue marker. Anchored at its centre so it sits over the fix.
 */
function userLocationIcon(): L.DivIcon {
  return L.divIcon({
    className: "", // suppress leaflet-div-icon's default white background box
    html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${MARKER_OPEN};border:3px solid #fff;box-shadow:0 0 0 2px ${MARKER_OPEN};"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// The visible pin (24×26) is well under the ~44px touch-target guideline, so
// each marker's actual icon is a larger transparent hit-box (MARKER_HIT_*)
// with the pin glyph flush against its bottom-center — same visual position
// (the tip still lands exactly on the coordinate), bigger tappable area.
const MARKER_HIT_W = 44;
const MARKER_HIT_H = Math.round(MARKER_HIT_W * PIN_RATIO);

function markerIcon(pool: Pool): L.DivIcon {
  const color = markerColor(pool);
  // Both kinds use a teardrop pin tinted by status; splash pads carry the spray
  // glyph so they stay visually distinct from the children's-pool pins. The pin
  // tip sits at the coordinate (anchor at the bottom-center of the hit-box).
  const glyph = pool.kind === "splash" ? splashPinSvg(24) : poolPinSvg(24);
  return L.divIcon({
    className: pool.kind === "splash" ? "splash-marker" : "pool-marker",
    html:
      `<span class="map-pin-hit" style="width:${MARKER_HIT_W}px;height:${MARKER_HIT_H}px">` +
      `<span class="map-pin" style="color:${color}">${glyph}</span></span>`,
    iconSize: [MARKER_HIT_W, MARKER_HIT_H],
    iconAnchor: [MARKER_HIT_W / 2, MARKER_HIT_H],
  });
}

// City center as a sane default if pools have no coordinates (CITY.mapCenter).
const CITY_CENTER: L.LatLngTuple = CITY.mapCenter;
// A big outer ring; paired with CITY.boundary as a hole it forms the dim mask
// that shades everything OUTSIDE the city (even-odd fill) without locking pan.
const WORLD_RING: L.LatLngTuple[] = [
  [-90, -200],
  [-90, 200],
  [90, 200],
  [90, -200],
];

export async function renderMap(app: HTMLElement, query: URLSearchParams): Promise<void> {
  const filters = parseFilters(query);

  // --- Phase 1: instant chrome (18c5d959, mirroring List/Splash) -----------
  // The header, legend, map canvas, tiles, boundary, and geo wiring depend
  // only on CITY + the parsed filters, so they all paint before the catalog
  // request resolves; the markers (and the offline banner) land in phase 2.
  app.innerHTML = `
    <header class="topbar">
      <div class="topbar-head">
        <h1>${CITY.displayName}</h1>
        <p class="header-contact">${geoButtonHtml()}<a href="#/about">Contact</a><a href="${CITY.donateUrl}" target="_blank" rel="noopener">Donate</a></p>
      </div>
      <p class="tagline">${CITY.listTagline}</p>
      <p class="geo-banner" data-role="geo-banner" hidden></p>
      <div data-role="dev-banner"></div>
      ${viewTabs("map", filters)}
    </header>
    <main>
      <div id="map" class="map"></div>
      <p class="map-legend">
        <span class="legend-dot" style="background:${MARKER_OPEN}"></span> Open
        <span class="legend-dot" style="background:${MARKER_CLOSED}"></span> Closed / conflict
        <span class="legend-dot" style="background:${MARKER_NEUTRAL}"></span> Closed for season
        <span class="legend-drop">${poolPinSvg(15)}</span> Pool
        <span class="legend-drop">${splashPinSvg(15)}</span> Splash pad
        <span class="legend-dot" style="background:${MARKER_OPEN};border:2.5px solid #fff;box-shadow:0 0 0 2px ${MARKER_OPEN};"></span> You are here
      </p>
    </main>`;

  const mapEl = app.querySelector<HTMLElement>("#map")!;
  // Focus the map on the city without caging it: the user can freely pan/zoom,
  // while an outline + dimmed surround keep the city the visual subject.
  const map = L.map(mapEl, {
    minZoom: 10,
  }).setView(CITY_CENTER, CITY.mapZoom);
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap contributors",
  }).addTo(map);

  // Dim everything outside the city: WORLD_RING is the outer ring, CITY.boundary
  // the hole, so even-odd fill shades only the surround. Added after the tiles
  // (so they show through the translucent dim) and before the markers (so pins
  // stay on top). Both layers are non-interactive so they never eat clicks.
  L.polygon([WORLD_RING, CITY.boundary], {
    fillColor: "#0f172a",
    fillOpacity: 0.18,
    stroke: false,
    interactive: false,
  }).addTo(map);
  L.polygon(CITY.boundary, {
    fill: false,
    color: "#2563eb",
    weight: 2,
    opacity: 0.7,
    interactive: false,
  }).addTo(map);

  // User-location marker — reuse the shared session grant from geo.ts. Contract:
  //   • cachedLocation() non-null  → fix already available this session; add immediately.
  //   • hasLocationOptIn() true    → user consented in a prior visit; resolve silently
  //     (the browser won't re-prompt once permission is granted) then add when it lands.
  //   • neither                    → first-time visitor; NEVER auto-prompt here.
  // Guard against double-add (async path could race with a re-render).
  let _userMarker: L.Marker | null = null;
  function placeUserMarker(loc: LatLng): void {
    if (_userMarker) return;
    _userMarker = L.marker([loc.lat, loc.lng], {
      icon: userLocationIcon(),
      zIndexOffset: 1000, // sit above all pool/splash pins
    })
      .addTo(map)
      .bindTooltip("You are here", { permanent: false, direction: "top" });
  }

  // --- "Nearby" pill: reuse/request the shared session fix, place the user
  // marker, and (only on an explicit tap — see onFix below) recenter/zoom the
  // map on it. Mirrors the List/Splash button states and denied/unavailable
  // banner copy so the control feels identical across tabs.
  const geoBtn = app.querySelector<HTMLButtonElement>('[data-action="locate"]');
  const geoBanner = app.querySelector<HTMLElement>('[data-role="geo-banner"]');

  const onFix = (fix: LatLng | null): void => {
    if (fix) {
      placeUserMarker(fix);
      map.setView([fix.lat, fix.lng], 14);
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_LOCATED;
        geoBtn.disabled = true;
      }
      if (geoBanner) {
        geoBanner.hidden = true;
        geoBanner.textContent = "";
      }
    } else {
      if (geoBtn) {
        geoBtn.textContent = GEO_LABEL_IDLE;
        geoBtn.disabled = false;
      }
      if (geoBanner) {
        geoBanner.textContent =
          lastLocationError() === "denied" ? GEO_DENIED_COPY : "Location unavailable.";
        geoBanner.hidden = false;
      }
    }
  };

  geoBtn?.addEventListener("click", () => {
    geoBtn.disabled = true;
    geoBtn.textContent = GEO_LABEL_LOCATING;
    void getUserLocation().then(onFix);
  });

  const cachedLoc = cachedLocation();
  if (cachedLoc) {
    // A session fix already exists (set on a prior tab) — place the marker and
    // reflect it on the button, but don't recenter: the default view here is
    // the CITY-boundary fit below, and the user didn't tap "Nearby" on this
    // tab, so jumping the map to their location would be a surprise.
    placeUserMarker(cachedLoc);
    if (geoBtn) {
      geoBtn.textContent = GEO_LABEL_LOCATED;
      geoBtn.disabled = true;
    }
  } else if (hasLocationOptIn()) {
    // Returning opted-in user: resolve silently (the browser won't re-prompt
    // once permission is granted); a first-time visitor is never auto-prompted.
    void getUserLocation().then((loc) => {
      if (loc) {
        placeUserMarker(loc);
        if (geoBtn) {
          geoBtn.textContent = GEO_LABEL_LOCATED;
          geoBtn.disabled = true;
        }
      }
    });
  }

  // Fit to the CITY boundary, not to the marker bounds: fitting all markers
  // let a single far-flung coordinate (or just a naturally spread-out catalog)
  // drag the viewport out well past the city, which read as "zoomed too far
  // out" on a phone. The boundary fit keeps the city filling the screen; any
  // marker outside it is still reachable by panning. Done in phase 1 so the
  // map frames the city while the catalog is still loading.
  map.fitBounds(L.latLngBounds(CITY.boundary).pad(0.02));

  // --- Phase 2: the catalog lands — markers + the offline notice -----------
  const { pools, live } = await loadPools();

  const filtered = pools.filter((p) => {
    if (!matchesStaticFilters(p, filters)) return false;
    return true;
  });

  const devBanner = app.querySelector<HTMLElement>('[data-role="dev-banner"]');
  if (devBanner)
    devBanner.innerHTML = live
      ? ""
      : '<p class="dev-banner">Showing sample data. Backend not connected yet.</p>';

  const markers: Array<{ pool: Pool; marker: L.Marker }> = [];

  for (const pool of filtered) {
    if (typeof pool.lat !== "number" || typeof pool.lng !== "number") continue;
    const marker = L.marker([pool.lat, pool.lng], { icon: markerIcon(pool) }).addTo(map);
    marker.bindPopup(
      `<strong>${esc(pool.name)}</strong><br>${addressHtml(pool.address)}<br>` +
        `<a class="popup-link" href="#/pool/${encodeURIComponent(pool.poolId)}">View details →</a>`,
    );
    marker.bindTooltip(tooltipHtml(pool), { direction: "top", opacity: 0.95 });
    // Deliberately no click→navigate handler here: Leaflet's own marker click
    // already opens the bound popup. A separate navigate() on click used to
    // fire in addition to that, so on touch the navigation beat the popup to
    // the screen and it never showed. First tap now opens the popup (name +
    // address + "View details →"); the link inside it does the navigating.
    markers.push({ pool, marker });
  }

  // Warm every status with one batch call, then recolor all markers at once
  // (replaces the former per-pool fan-out).
  if (live) {
    loadAllStatuses().then(() => {
      for (const { pool, marker } of markers) {
        if (pool.closedForSeason || !cachedStatus(pool.poolId)) continue;
        marker.setIcon(markerIcon(pool));
        marker.setTooltipContent(tooltipHtml(pool));
      }
    });
  }
}
